import React, { useState, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
  Paper,
  Autocomplete,
} from '@mui/material';
import {
  AddCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Today,
  AccessTime,
  LocationOn,
} from '@mui/icons-material';
import { calendarApi, locationsApi } from '../services/api';

// Site uses Eastern timezone (America/New_York) for all operations
const SITE_TIMEZONE = 'America/New_York';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type CalendarEvent = {
  id: string;
  summary: string;
  description: string;
  location: string;
  start: { dateTime?: string; date?: string; timeZone?: string };
  end: { dateTime?: string; date?: string; timeZone?: string };
  htmlLink: string;
};

function getEventDate(event: CalendarEvent): string {
  // date is for all-day events, dateTime for timed events
  const raw = event.start.dateTime || event.start.date || '';
  return raw.slice(0, 10); // YYYY-MM-DD
}

function formatTime(dtStr?: string): string {
  if (!dtStr) return '';
  try {
    const d = new Date(dtStr);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: SITE_TIMEZONE,
    });
  } catch {
    return '';
  }
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

// Consistent event colors based on event id
const EVENT_COLORS = [
  '#1976d2', '#388e3c', '#f57c00', '#7b1fa2',
  '#c62828', '#00838f', '#4e342e', '#283593',
];

function eventColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return EVENT_COLORS[Math.abs(hash) % EVENT_COLORS.length];
}

const Calendar: React.FC = () => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    summary: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    location: '',
  });

  // Compute time range for API query (month -1 to month +1 for buffer)
  const timeRange = useMemo(() => {
    const start = new Date(viewYear, viewMonth - 1, 1);
    const end = new Date(viewYear, viewMonth + 2, 0);
    return {
      timeMin: start.toISOString(),
      timeMax: end.toISOString(),
    };
  }, [viewYear, viewMonth]);

  // Fetch events from backend (server-side Google Calendar API call)
  const { data: eventsData, isLoading, refetch } = useQuery(
    ['calendarEvents', timeRange.timeMin, timeRange.timeMax],
    () => calendarApi.getEvents(timeRange.timeMin, timeRange.timeMax),
    { retry: 1 }
  );

  const events = eventsData?.events || [];

  // Fetch managed locations for the Autocomplete suggestions
  const { data: locationsData } = useQuery(['locations'], () =>
    locationsApi.getLocations({ is_active: true }));
  const locationOptions = locationsData?.locations.map((l) => l.name) || [];

  // Group events by date for quick lookup
  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    events.forEach((ev) => {
      const dateKey = getEventDate(ev);
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(ev);
    });
    return map;
  }, [events]);

  // Calendar grid
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const calendarCells: Array<{ day: number | null; dateStr: string }> = [];
  for (let i = 0; i < firstDay; i++) {
    calendarCells.push({ day: null, dateStr: '' });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    calendarCells.push({ day: d, dateStr });
  }
  // Pad to fill last row
  while (calendarCells.length % 7 !== 0) {
    calendarCells.push({ day: null, dateStr: '' });
  }

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(viewYear - 1); setViewMonth(11); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(viewYear + 1); setViewMonth(0); }
    else setViewMonth(viewMonth + 1);
  };
  const goToday = () => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); };

  const createEventMutation = useMutation(calendarApi.createEvent, {
    onSuccess: (data) => {
      setSuccess(`Event "${data.summary}" created successfully!`);
      setError('');
      setDialogOpen(false);
      setFormData({ summary: '', description: '', start_date: '', start_time: '', end_date: '', end_time: '', location: '' });
      setTimeout(() => setSuccess(''), 5000);
      refetch();
    },
    onError: (error: any) => {
      setError(error.response?.data?.detail || 'Failed to create event');
      setSuccess('');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.summary.trim()) { setError('Event title is required'); return; }
    if (!formData.start_date || !formData.start_time) { setError('Start date and time are required'); return; }
    if (!formData.end_date || !formData.end_time) { setError('End date and time are required'); return; }

    const start_datetime = `${formData.start_date}T${formData.start_time}:00`;
    const end_datetime = `${formData.end_date}T${formData.end_time}:00`;

    createEventMutation.mutate({
      summary: formData.summary,
      description: formData.description || undefined,
      start_datetime,
      end_datetime,
      location: formData.location || undefined,
      timezone: SITE_TIMEZONE,
    });
  };

  const todayDefault = today.toISOString().split('T')[0];

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Company Calendar
        </Typography>
        <Typography variant="body1" color="textSecondary" gutterBottom>
          View company events, schedules, and important dates.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircle />} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 3 }}>
          <Button variant="contained" startIcon={<AddCircle />} onClick={() => setDialogOpen(true)} size="large">
            Add Event
          </Button>
        </Box>

        {/* Month navigation */}
        <Card sx={{ mt: 1 }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={prevMonth}><ChevronLeft /></IconButton>
                <Typography variant="h5" sx={{ minWidth: 200, textAlign: 'center' }}>
                  {MONTH_NAMES[viewMonth]} {viewYear}
                </Typography>
                <IconButton onClick={nextMonth}><ChevronRight /></IconButton>
              </Box>
              <Button startIcon={<Today />} onClick={goToday} size="small" variant="outlined">
                Today
              </Button>
            </Box>

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Day-of-week header */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', bgcolor: 'divider' }}>
                  {DAY_NAMES.map((d) => (
                    <Box key={d} sx={{ bgcolor: 'grey.100', py: 1, textAlign: 'center' }}>
                      <Typography variant="subtitle2" color="text.secondary">{d}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Calendar grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', bgcolor: 'divider', border: '1px solid', borderColor: 'divider' }}>
                  {calendarCells.map((cell, idx) => {
                    const dayEvents = cell.dateStr ? (eventsByDate[cell.dateStr] || []) : [];
                    const isToday = cell.dateStr === todayStr;
                    return (
                      <Box
                        key={idx}
                        sx={{
                          bgcolor: 'background.paper',
                          minHeight: 100,
                          p: 0.5,
                          opacity: cell.day ? 1 : 0.3,
                          position: 'relative',
                        }}
                      >
                        {cell.day && (
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: isToday ? 700 : 400,
                                color: isToday ? 'primary.contrastText' : 'text.primary',
                                bgcolor: isToday ? 'primary.main' : 'transparent',
                                borderRadius: '50%',
                                width: 28,
                                height: 28,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 0.5,
                              }}
                            >
                              {cell.day}
                            </Typography>
                            {dayEvents.map((ev) => (
                              <Tooltip
                                key={ev.id}
                                title={
                                  <Box>
                                    <Typography variant="subtitle2">{ev.summary}</Typography>
                                    {ev.start.dateTime && (
                                      <Typography variant="caption">
                                        {formatTime(ev.start.dateTime)} - {formatTime(ev.end.dateTime)}
                                      </Typography>
                                    )}
                                    {ev.location && (
                                      <Typography variant="caption" display="block">{ev.location}</Typography>
                                    )}
                                    {ev.description && (
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{ev.description}</Typography>
                                    )}
                                  </Box>
                                }
                                arrow
                              >
                                <Chip
                                  label={
                                    ev.start.dateTime
                                      ? `${formatTime(ev.start.dateTime)} ${ev.summary}`
                                      : ev.summary
                                  }
                                  size="small"
                                  onClick={() => setSelectedEvent(ev)}
                                  sx={{
                                    width: '100%',
                                    justifyContent: 'flex-start',
                                    mb: 0.25,
                                    bgcolor: eventColor(ev.id),
                                    color: '#fff',
                                    fontSize: '0.7rem',
                                    height: 20,
                                    cursor: 'pointer',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    },
                                  }}
                                />
                              </Tooltip>
                            ))}
                          </>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Event detail dialog */}
      <Dialog open={!!selectedEvent} onClose={() => setSelectedEvent(null)} maxWidth="sm" fullWidth>
        {selectedEvent && (
          <>
            <DialogTitle>{selectedEvent.summary}</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                {selectedEvent.start.dateTime && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime fontSize="small" color="action" />
                    <Typography variant="body2">
                      {new Date(selectedEvent.start.dateTime).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: SITE_TIMEZONE })}
                      {' '}
                      {formatTime(selectedEvent.start.dateTime)} - {formatTime(selectedEvent.end.dateTime)}
                    </Typography>
                  </Box>
                )}
                {selectedEvent.start.date && !selectedEvent.start.dateTime && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime fontSize="small" color="action" />
                    <Typography variant="body2">
                      {new Date(selectedEvent.start.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                      {' (All day)'}
                    </Typography>
                  </Box>
                )}
                {selectedEvent.location && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2">{selectedEvent.location}</Typography>
                  </Box>
                )}
                {selectedEvent.description && (
                  <Paper variant="outlined" sx={{ p: 1.5, mt: 1 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedEvent.description}
                    </Typography>
                  </Paper>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Add Event Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Calendar Event</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth label="Event Title" value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              margin="normal" required disabled={createEventMutation.isLoading}
            />
            <TextField
              fullWidth label="Description" value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal" multiline rows={3} disabled={createEventMutation.isLoading}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth label="Start Date" type="date" value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                margin="normal" required disabled={createEventMutation.isLoading}
                InputLabelProps={{ shrink: true }} inputProps={{ min: todayDefault }}
              />
              <TextField
                fullWidth label="Start Time" type="time" value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                margin="normal" required disabled={createEventMutation.isLoading}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth label="End Date" type="date" value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                margin="normal" required disabled={createEventMutation.isLoading}
                InputLabelProps={{ shrink: true }} inputProps={{ min: formData.start_date || todayDefault }}
              />
              <TextField
                fullWidth label="End Time" type="time" value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                margin="normal" required disabled={createEventMutation.isLoading}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Autocomplete
              freeSolo
              options={locationOptions}
              value={formData.location}
              onInputChange={(_e, value) => setFormData({ ...formData, location: value })}
              disabled={createEventMutation.isLoading}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Location (Optional)" margin="normal" />
              )}
            />
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit" variant="contained" disabled={createEventMutation.isLoading} fullWidth
                startIcon={createEventMutation.isLoading ? <CircularProgress size={20} /> : <AddCircle />}
              >
                {createEventMutation.isLoading ? 'Creating...' : 'Create Event'}
              </Button>
              <Button variant="outlined" onClick={() => setDialogOpen(false)} disabled={createEventMutation.isLoading}>
                Cancel
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default Calendar;
